import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Card, Col, Descriptions, Dropdown, Popconfirm, Row, Space, Table, Tabs, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined, StarOutlined } from '@ant-design/icons';
import { palette } from '../../app/theme';
import { CardTitle } from '../../components/StatCard';
import { NameCell } from '../../components/NameCell';
import type { ContactListItem, FieldDefinitionDto } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { Timeline } from '../../components/Timeline';
import { TaskList } from '../../components/TaskList';
import { useFields } from '../../features/fields/api';
import { useDeleteOrganizations, useOrganization, useOrganizationContacts, useUpdateOrganization } from '../../features/organizations/api';
import { contactName } from '../../features/contacts/api';
import { errorMessage, fileUrl } from '../../api/client';
import { PhotoAvatar } from '../../components/PhotoAvatar';
import { OrganizationDrawer } from './OrganizationDrawer';
import { renderOrgCell } from './OrganizationsPage';
import { QuickCreateModal } from '../../components/QuickCreateModal';
import { useQueryClient } from '@tanstack/react-query';
import { renderCell } from '../contacts/ContactsPage';
import { DealsTab } from '../../components/DealsTab';
import { EmptyState, PageSkeleton, RecordError, tableLoading, tableLocale } from '../../components/states';
import { splitFieldGroups, ViewMoreCard } from '../../components/ViewMore';
import { AddressList } from '../../components/Addresses';
import { LinksView, toLinks } from '../../components/LinksField';
import { useFeatures } from '../../features/settings/api';

export function OrganizationProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const features = useFeatures();
  const org = useOrganization(id);
  const defs = useFields('ORGANIZATION');
  const contactDefs = useFields('CONTACT');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const people = useOrganizationContacts(id, { page, pageSize, sortBy: 'last_name', sortDir: 'asc' });
  const remove = useDeleteOrganizations();
  const update = useUpdateOrganization();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [addContact, setAddContact] = useState(false);
  const preset = useMemo(() => ({ organizationId: id }), [id]);

  const groups = useMemo(() => splitFieldGroups(defs.data ?? [], (d) => d.key === 'name'), [defs.data]);

  if (org.isError) return <RecordError error={org.error} what="Organization" backTo="/organizations" backLabel="Back to organizations" onRetry={org.refetch} />;
  if (org.isLoading || !org.data) return <PageSkeleton />;
  const o = org.data;
  const makeMain = (c: ContactListItem) =>
    update.mutateAsync({ id: o.id, mainContactId: c.id }).then(() => message.success(`${contactName(c)} is now the main contact`)).catch((e) => message.error(errorMessage(e)));
  const peopleCols = ['email', 'lifecycle_stage', 'email_status'].map((k) => (contactDefs.data ?? []).find((d) => d.key === k)).filter((d): d is FieldDefinitionDto => !!d);

  const renderFields = (fields: FieldDefinitionDto[]) => (
    <Descriptions className="field-list" column={1} size="small" colon={false} styles={{ label: { width: 130, color: palette.textSecondary }, content: { minWidth: 0, wordBreak: 'break-word' } }}>
      {fields.map((f) => (
        <Descriptions.Item key={f.key} label={f.label}>
          {(f.type === 'LINKS' ? toLinks(o.fields?.[f.key]).length > 0 && <LinksView value={o.fields?.[f.key]} /> : renderOrgCell(f, o)) || <Typography.Text type="secondary">—</Typography.Text>}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );

  return (
    <div className="page">
      <PageHeader
        breadcrumb={[{ title: 'Organizations', to: '/organizations' }, { title: o.name }]}
        title={o.name}
        subtitle={[o.industry, o.domain].filter(Boolean).join(' · ')}
        extra={
          <Space>
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>Edit</Button>
            <Popconfirm title="Delete this organization? Its contacts are kept without an organization." okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => remove.mutateAsync([o.id]).then(() => { message.success('Organization deleted'); navigate('/organizations'); }).catch((e) => message.error(errorMessage(e)))}>
              <Button danger icon={<DeleteOutlined />}>Delete</Button>
            </Popconfirm>
          </Space>
        }
      />
      <Row gutter={24}>
        <Col xs={24} lg={9}>
          <Card style={{ marginBottom: 24 }}>
            <Space align="center" size={14}>
              <PhotoAvatar kind="organization" recordId={o.id} fileId={o.logoFileId} fallback={(o.name[0] ?? '?').toUpperCase()} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 16 }}>{o.name}</div>
                {(o.domain ?? o.industry) && <Typography.Text type="secondary">{o.domain ?? o.industry}</Typography.Text>}
              </div>
            </Space>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 20 }}>
              {[
                { label: 'Contacts', value: o.contactCount },
                { label: 'Deals', value: o.counts.deals },
              ].map((x) => (
                <div key={x.label} style={{ background: palette.canvas, borderRadius: 10, padding: '10px 14px' }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>{x.label}</Typography.Text>
                  <div style={{ fontSize: 20, fontWeight: 600 }}>{x.value.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card title={<CardTitle title="Main contact" />} style={{ marginBottom: 24 }}>
            {o.mainContact ? (
              <NameCell name={contactName(o.mainContact)} sub={o.mainContact.jobTitle ?? o.mainContact.email} to={`/contacts/${o.mainContact.id}`} image={fileUrl(o.mainContact.photoFileId)} size={32} />
            ) : (
              <Typography.Text type="secondary">No contacts at this organization yet.</Typography.Text>
            )}
          </Card>
          <Card title={<CardTitle title="Addresses" />} style={{ marginBottom: 24 }}>
            <AddressList items={o.addresses} />
          </Card>
          {groups.main.map(([group, fields]) => (
            <Card key={group} title={<CardTitle title={group} />} style={{ marginBottom: 24 }}>
              {renderFields(fields)}
            </Card>
          ))}
          <ViewMoreCard groups={groups.more} render={renderFields} />
        </Col>
        <Col xs={24} lg={15}>
          <Card styles={{ body: { paddingTop: 8 } }}>
            <Tabs
              items={[
                {
                  key: 'contacts',
                  label: `Contacts (${o.contactCount})`,
                  children: (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                        <Button icon={<PlusOutlined />} onClick={() => setAddContact(true)}>Add contact</Button>
                      </div>
                      <Table<ContactListItem>
                        rowKey="id"
                        loading={tableLoading(people)}
                        scroll={{ x: 'max-content' }}
                        locale={tableLocale(people, <EmptyState description="No contacts at this organization yet." action={<Button icon={<PlusOutlined />} onClick={() => setAddContact(true)}>Add contact</Button>} />)}
                        dataSource={people.data?.items ?? []}
                        pagination={{ current: page, pageSize, total: people.data?.total ?? 0, showSizeChanger: true, onChange: (p, s) => { setPage(s === pageSize ? p : 1); setPageSize(s); }, hideOnSinglePage: true }}
                        columns={[
                          {
                            title: 'Name',
                            key: 'name',
                            render: (_, c) => (
                              <Space size={8}>
                                <NameCell name={contactName(c)} sub={c.jobTitle} to={`/contacts/${c.id}`} image={fileUrl(c.photoFileId)} size={32} />
                                {c.id === o.mainContactId && <Tag color="blue">Main contact</Tag>}
                              </Space>
                            ),
                          },
                          ...peopleCols.map((f) => ({ title: f.label, key: f.key, ellipsis: true, render: (_: unknown, c: ContactListItem) => renderCell(f, c) })),
                          {
                            key: 'actions',
                            width: 48,
                            align: 'right' as const,
                            render: (_: unknown, c: ContactListItem) =>
                              c.id !== o.mainContactId && (
                                <Dropdown trigger={['click']} menu={{ items: [{ key: 'main', icon: <StarOutlined />, label: 'Make main contact', onClick: () => void makeMain(c) }] }}>
                                  <Button type="text" size="small" icon={<MoreOutlined />} aria-label="Contact actions" loading={update.isPending && update.variables?.mainContactId === c.id} />
                                </Dropdown>
                              ),
                          },
                        ]}
                      />
                    </div>
                  ),
                },
                { key: 'timeline', label: 'Timeline', children: <Timeline organizationId={o.id} fields={[...(defs.data ?? []), ...(contactDefs.data ?? [])]} showContact /> },
                { key: 'tasks', label: `Tasks${o.counts.openTasks ? ` (${o.counts.openTasks})` : ''}`, children: <TaskList organizationId={o.id} /> },
                ...(features.enabled('pipelines') ? [{ key: 'deals', label: `Deals (${o.counts.deals})`, children: <DealsTab organizationId={o.id} /> }] : []),
              ]}
            />
          </Card>
        </Col>
      </Row>
      <OrganizationDrawer open={editing} onClose={() => setEditing(false)} defs={defs.data ?? []} organization={o} />
      <QuickCreateModal
        objectType="CONTACT"
        open={addContact}
        onClose={() => setAddContact(false)}
        preset={preset}
        onCreated={() => {
          void qc.invalidateQueries({ queryKey: ['organization-contacts', o.id] });
          void qc.invalidateQueries({ queryKey: ['organization', o.id] });
        }}
      />
    </div>
  );
}
